import React, { CSSProperties, Suspense } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { capitalize } from 'lodash'
import dayjs from 'dayjs'
import { colors } from '@lmc/common/constants'
import { ProfilePicture, ProfileBadge } from '../generic'
import Field from '../fields'
import { isBrowser, isTablet } from 'react-device-detect'
import { getDolsIcon } from '../../screens/Residents/screens/ResidentProfileScreen/_legacyReidentProfileScreen/components/ResidentProfilePrimary'

interface ResidentQuoteBoxProps {
  fieldProps?: any // Update the type to match your fieldProps shape
  resident: any // Update the type to match your resident shape
  style?: React.CSSProperties
  text?: string
  textStyle?: React.CSSProperties
  features: any // Update the type to match your features shape
}

const ResidentQuoteBox: React.FC<ResidentQuoteBoxProps> = ({
  fieldProps,
  resident,
  style,
  text,
  textStyle,
  features,
}) => {
  if (!resident) return null

  const dateOfBirth = dayjs(resident.dateOfBirth).format('Do MMMM YYYY')
  const age = dayjs().diff(dayjs(resident.dateOfBirth), 'years')
  const fullName = `${resident.name.first} ${resident.name.last}`
  const status =
    resident.status === 'pre-assessment'
      ? 'Initial Assessment'
      : resident.status
  const hasBadges = isDesktop && (resident.dnacpr || resident.dols)

  return (
    <div
      className="lmc-card"
      style={{
        ...styles.wrapper,
        ...style,
        display: isDesktop ? 'flex' : 'block',
      }}
    >
      {isDesktop && renderProfilePicture(resident.picture)}
      <div style={styles.textContainer}>
        {fieldProps
          ? renderInput(fieldProps, textStyle as CSSProperties)
          : renderText(text!, textStyle as CSSProperties)}
        <div style={styles.residentInfoContainer}>
          {!isDesktop && renderProfilePicture(resident.picture)}
          <span style={styles.residentInfo}>
            {fullName}, {dateOfBirth} ({age}) • {capitalize(status)}
          </span>
        </div>
      </div>
      {hasBadges && renderBadges(resident, features)}
    </div>
  )
}

const renderProfilePicture = (url: string) => (
  <ProfilePicture
    url={url}
    style={isDesktop ? styles.residentImage : styles.residentImageSmall}
    type={isDesktop ? 'large' : 'handover__small'}
  />
)

const renderInput = (fieldProps: any, textStyle: React.CSSProperties) => (
  <Suspense fallback={<div>Loading...</div>}>
  <Field
    className="lmc-sub-card-edit"
    style={{
      ...styles.text,
      ...styles.input,
      ...textStyle,
    }}
    {...fieldProps}
  />
  </Suspense>
)

const renderText = (text: string, textStyle: React.CSSProperties) => (
  <span
    style={{
      ...styles.text,
      ...textStyle,
    }}
  >
    "{text}"
  </span>
)

const renderBadges = (resident: any, features: any) => {
  const dolsIcon = getDolsIcon(
    resident.dols,
    resident.dolsEndDate,
    features.countryCode
  )
  return (
    <div style={styles.badgeContainer}>
      {resident.dnacpr && (
        <div>
          <ProfileBadge
            badgeColor={colors.turquoise}
            badgeText="DNACPR"
            badgeStyle={styles.badge}
          />
        </div>
      )}
      {resident.dols && (
        <div style={styles.dolsContainer as CSSProperties}>{dolsIcon}</div>
      )}
    </div>
  )
}

const isDesktop = isBrowser || isTablet

const styles = {
  dolsContainer: {
    marginTop: 5,
    textAlign: 'right',
  },
  badge: {
    fontSize: 12,
    fontWeight: 'bold',
    padding: '3px 12px',
    textAlign: 'center',
    bottom: 6,
    right: 0,
    borderRadius: 15,
  },
  badgeContainer: {
    marginLeft: 24,
  },
  input: {
    height: 100,
    minHeight: 100,
    marginTop: 0,
    width: '100%',
  },
  residentImage: {
    flexShrink: 0,
    margin: '0px 24px 0px 0px',
  },
  residentImageSmall: {
    flexShrink: 0,
    marginRight: 15,
  },
  residentInfo: {
    color: colors.black,
  },
  residentInfoContainer: {
    display: 'flex',
    marginTop: 15,
  },
  text: {
    color: colors.black,
    display: 'block',
    marginTop: 5,
    maxHeight: 220,
    fontWeight: 300,
    overflow: 'auto',
    fontSize: isDesktop ? 28 : 20,
  },
  textContainer: {
    width: '100%',
  },
  wrapper: {
    padding: 24,
  },
}

interface RootState {
  global: {
    home: any // Update the type to match your global state shape
  }
}

const mapStateToProps = (state: RootState) => ({
  features: state.global.home,
})

export default connect(mapStateToProps, null)(ResidentQuoteBox)
